import { useState, useEffect, useCallback } from "react";
import { getStores, addStore, updateStore, deleteStore, syncStoresFromPOs, syncStoresFromStock } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Store, Plus, Trash2, RefreshCw, Search, Edit2, X, Check, MapPin, Mail, Phone, Building2, Users
} from "lucide-react";
import { toast } from "sonner";

export default function StoreDirectory() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [newStore, setNewStore] = useState({ store_name: "", address: "", email: "", phone: "", gst: "", city: "", pincode: "", contact_person: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getStores();
      setStores(r.data.stores || []);
    } catch {
      toast.error("Failed to load stores");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = stores.filter(s =>
    s.store_name?.toLowerCase().includes(search.toLowerCase()) ||
    s.city?.toLowerCase().includes(search.toLowerCase()) ||
    s.contact_person?.toLowerCase().includes(search.toLowerCase())
  );

  const handleSyncPOs = async () => {
    setSyncing(true);
    try {
      const r = await syncStoresFromPOs();
      toast.success(r.data.message);
      await load();
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };

  const handleSyncStock = async () => {
    setSyncing(true);
    try {
      const r = await syncStoresFromStock();
      toast.success(r.data.message);
      await load();
    } catch { toast.error("Sync failed"); }
    setSyncing(false);
  };

  const handleAdd = async () => {
    if (!newStore.store_name.trim()) { toast.error("Store name is required"); return; }
    try {
      await addStore(newStore);
      toast.success(`Store "${newStore.store_name}" added`);
      setNewStore({ store_name: "", address: "", email: "", phone: "", gst: "", city: "", pincode: "", contact_person: "" });
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add store");
    }
  };

  const handleUpdate = async () => {
    if (!editingStore) return;
    try {
      await updateStore(editingStore.store_name, editingStore);
      toast.success(`Store "${editingStore.store_name}" updated`);
      setEditingStore(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update store");
    }
  };

  const handleDelete = async (storeName) => {
    if (!window.confirm(`Remove "${storeName}" from the directory?`)) return;
    try {
      await deleteStore(storeName);
      toast.success(`"${storeName}" removed`);
      setStores(prev => prev.filter(s => s.store_name !== storeName));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const sourceBadge = (source) => {
    const map = {
      manual: { label: "Manual", cls: "bg-slate-100 text-slate-700" },
      po_upload: { label: "PO Upload", cls: "bg-blue-100 text-blue-700" },
      po_sync: { label: "PO Sync", cls: "bg-indigo-100 text-indigo-700" },
      stock_upload: { label: "Stock Data", cls: "bg-emerald-100 text-emerald-700" },
      sales_data: { label: "Sales Data", cls: "bg-purple-100 text-purple-700" },
    };
    const info = map[source] || { label: source || "Unknown", cls: "bg-slate-100 text-slate-600" };
    return <Badge className={`text-[10px] ${info.cls}`}>{info.label}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="store-directory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Store className="w-5 h-5" /> Store Directory
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{stores.length} stores with contact details</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleSyncPOs} disabled={syncing} data-testid="sync-pos-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            Sync from POs
          </Button>
          <Button variant="outline" size="sm" onClick={handleSyncStock} disabled={syncing} data-testid="sync-stock-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            Sync from Stock
          </Button>
          <Button size="sm" onClick={() => setShowAddForm(!showAddForm)} data-testid="add-store-toggle-btn">
            <Plus className="w-4 h-4 mr-1.5" />
            Add Store
          </Button>
        </div>
      </div>

      {/* Add Store Form */}
      {showAddForm && (
        <Card data-testid="add-store-form">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm">Add New Store</CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <Input placeholder="Store Name *" value={newStore.store_name}
                onChange={e => setNewStore({ ...newStore, store_name: e.target.value })}
                data-testid="new-store-name" />
              <Input placeholder="Contact Person" value={newStore.contact_person}
                onChange={e => setNewStore({ ...newStore, contact_person: e.target.value })}
                data-testid="new-store-contact" />
              <Input placeholder="Email" value={newStore.email} type="email"
                onChange={e => setNewStore({ ...newStore, email: e.target.value })}
                data-testid="new-store-email" />
              <Input placeholder="Phone" value={newStore.phone}
                onChange={e => setNewStore({ ...newStore, phone: e.target.value })}
                data-testid="new-store-phone" />
              <Input placeholder="Address" value={newStore.address} className="md:col-span-2"
                onChange={e => setNewStore({ ...newStore, address: e.target.value })}
                data-testid="new-store-address" />
              <Input placeholder="City" value={newStore.city}
                onChange={e => setNewStore({ ...newStore, city: e.target.value })}
                data-testid="new-store-city" />
              <Input placeholder="Pincode" value={newStore.pincode}
                onChange={e => setNewStore({ ...newStore, pincode: e.target.value })}
                data-testid="new-store-pincode" />
              <Input placeholder="GST Number" value={newStore.gst}
                onChange={e => setNewStore({ ...newStore, gst: e.target.value })}
                data-testid="new-store-gst" />
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={handleAdd} data-testid="submit-add-store">
                <Check className="w-4 h-4 mr-1" /> Add Store
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowAddForm(false)}>
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search stores..." value={search} onChange={e => setSearch(e.target.value)}
          className="pl-9" data-testid="store-search" />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Store} label="Total Stores" value={stores.length} color="blue" />
        <StatCard icon={Mail} label="With Email" value={stores.filter(s => s.email).length} color="emerald" />
        <StatCard icon={Phone} label="With Phone" value={stores.filter(s => s.phone).length} color="purple" />
        <StatCard icon={MapPin} label="With Address" value={stores.filter(s => s.address).length} color="amber" />
      </div>

      {/* Store Table */}
      <Card className="overflow-hidden">
        <CardHeader className="py-3 px-4 border-b">
          <CardTitle className="text-sm">All Stores ({filtered.length})</CardTitle>
        </CardHeader>
        <ScrollArea className="max-h-[550px]">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <Store className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">{search ? "No stores match your search" : "No stores yet. Sync from POs or add manually."}</p>
            </div>
          ) : (
            <Table data-testid="store-directory-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs">Store</TableHead>
                  <TableHead className="text-xs">Contact</TableHead>
                  <TableHead className="text-xs">Email / Phone</TableHead>
                  <TableHead className="text-xs">City / Pincode</TableHead>
                  <TableHead className="text-xs">GST</TableHead>
                  <TableHead className="text-xs">Source</TableHead>
                  <TableHead className="text-xs w-20">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((s) => {
                  const isEditing = editingStore?.store_name === s.store_name;
                  return (
                    <TableRow key={s.store_name} className="hover:bg-slate-50" data-testid={`store-row-${s.store_name}`}>
                      <TableCell className="font-medium text-slate-800 text-sm max-w-[200px]">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          <span className="truncate">{s.store_name}</span>
                        </div>
                        {s.address && <p className="text-xs text-slate-400 mt-0.5 truncate ml-5">{s.address}</p>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {isEditing ? (
                          <Input value={editingStore.contact_person} className="h-7 text-xs"
                            onChange={e => setEditingStore({ ...editingStore, contact_person: e.target.value })} />
                        ) : (
                          <span className="text-slate-600">{s.contact_person || "-"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        {isEditing ? (
                          <div className="space-y-1">
                            <Input value={editingStore.email} className="h-7 text-xs" placeholder="email"
                              onChange={e => setEditingStore({ ...editingStore, email: e.target.value })} />
                            <Input value={editingStore.phone} className="h-7 text-xs" placeholder="phone"
                              onChange={e => setEditingStore({ ...editingStore, phone: e.target.value })} />
                          </div>
                        ) : (
                          <div>
                            {s.email && <p className="text-slate-600 flex items-center gap-1"><Mail className="w-3 h-3" />{s.email}</p>}
                            {s.phone && <p className="text-slate-500 flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{s.phone}</p>}
                            {!s.email && !s.phone && <span className="text-slate-300">-</span>}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {isEditing ? (
                          <div className="space-y-1">
                            <Input value={editingStore.city} className="h-7 text-xs" placeholder="city"
                              onChange={e => setEditingStore({ ...editingStore, city: e.target.value })} />
                            <Input value={editingStore.pincode} className="h-7 text-xs" placeholder="pincode"
                              onChange={e => setEditingStore({ ...editingStore, pincode: e.target.value })} />
                          </div>
                        ) : (
                          <span>{[s.city, s.pincode].filter(Boolean).join(" - ") || "-"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-slate-600">
                        {isEditing ? (
                          <Input value={editingStore.gst} className="h-7 text-xs"
                            onChange={e => setEditingStore({ ...editingStore, gst: e.target.value })} />
                        ) : (
                          s.gst || "-"
                        )}
                      </TableCell>
                      <TableCell>{sourceBadge(s.source)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {isEditing ? (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50"
                                onClick={handleUpdate} data-testid={`save-store-${s.store_name}`}>
                                <Check className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:bg-slate-100"
                                onClick={() => setEditingStore(null)}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                                onClick={() => setEditingStore({ ...s })} data-testid={`edit-store-${s.store_name}`}>
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDelete(s.store_name)} data-testid={`delete-store-${s.store_name}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    emerald: "bg-emerald-50 text-emerald-600",
    purple: "bg-purple-50 text-purple-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <div className="bg-white rounded-xl border p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-lg font-bold text-slate-800">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}
